/**
 * Czysta maszyna stanów obiektu (toggle / consume) — bez Phaser.
 * Stan sesyjny: reset przy wejściu do sceny (PLAN-V2 §3.1).
 */

import type { ObjectDef } from './types.js';

export type ObjectStateSnapshot = {
  defId: string;
  /** Indeks w def.states; 0 gdy brak stanów. */
  stateIndex: number;
  /** Nazwa stanu lub null. */
  state: string | null;
  /** Czy obiekt jest „zużyty" (consume bez stanów / ostatni stan empty). */
  consumed: boolean;
  /** Czy leży na ziemi (nie w pickup/hold). */
  onGround: boolean;
  /** Czy włączony (toggle). */
  toggledOn: boolean;
};

export function createObjectState(def: ObjectDef): ObjectStateSnapshot {
  const states = def.states ?? [];
  const state = states.length > 0 ? states[0]! : null;
  return {
    defId: def.id,
    stateIndex: 0,
    state,
    consumed: false,
    onGround: true,
    toggledOn: true,
  };
}

/** Przejście do następnego stanu (toggle / cykl). Wraca na 0 po ostatnim. */
export function advanceState(snap: ObjectStateSnapshot, def: ObjectDef): ObjectStateSnapshot {
  const states = def.states ?? [];
  if (states.length === 0) {
    return {
      ...snap,
      toggledOn: !snap.toggledOn,
    };
  }
  const nextIndex = (snap.stateIndex + 1) % states.length;
  const state = states[nextIndex]!;
  return {
    ...snap,
    stateIndex: nextIndex,
    state,
    toggledOn: nextIndex === 0 || state === 'full' || state === 'on',
    consumed: state === 'empty' || state === 'off' || state === 'used',
  };
}

/** Consume: przejdź do stanu „empty"/ostatniego lub oznacz consumed. */
export function consumeState(snap: ObjectStateSnapshot, def: ObjectDef): ObjectStateSnapshot {
  if (snap.consumed) return snap;
  const states = def.states ?? [];
  if (states.length === 0) {
    return { ...snap, consumed: true, toggledOn: false };
  }
  const emptyIdx = states.findIndex((s) => s === 'empty' || s === 'used' || s === 'off');
  const stateIndex = emptyIdx >= 0 ? emptyIdx : states.length - 1;
  const state = states[stateIndex]!;
  return {
    ...snap,
    stateIndex,
    state,
    consumed: true,
    toggledOn: false,
  };
}

/** Klucz tekstury dla aktualnego stanu (fallback: def.texture). */
export function textureKeyForState(def: ObjectDef, snap: ObjectStateSnapshot): string {
  if (snap.state && def.states && def.states.length > 0) {
    return `obj_${def.id}_${snap.state}`;
  }
  return def.texture;
}

/** Czy def ma dany rodzaj interakcji. */
export function hasInteraction(def: ObjectDef, kind: ObjectDef['interactions'][number]): boolean {
  return def.interactions.includes(kind);
}

/** Wymuś size ≥ MIN (walidacja katalogu). */
export function normalizeDefSize(size: number, min = 64): number {
  return Math.max(min, Math.floor(size));
}
